import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ALL_PRICES, MarketPrice } from '../_models/market-prices-data';

interface TopVariation {
    nameKey: string;
    categoryKey: string;
    price: string;
    change: string;
}

interface CategoryOption {
    key: string;      // stable English key used for filtering — DO NOT translate this
    labelKey: string;  // i18n key for display label
}

interface CountryOption {
    key: string;
    flag: string;
    labelKey: string;
}

interface BuyingTip {
    marketKey: string;
    cityKey: string;
    savingsPercent: number;
}

interface SnapshotGroup {
    catKey: string;
    items: MarketPrice[];
}

@Component({
    selector: 'app-market-prices',
    templateUrl: './market-prices.component.html',
    styleUrls: ['./market-prices.component.css'],
    standalone: false
})
export class MarketPricesComponent {
    readonly pageSize = 20;

    search = '';
    category = 'All';
    country = 'All';
    page = 1;

    readonly allPrices = ALL_PRICES;

    // Keep the actual last-updated timestamp as data, translate only the sentence around it
    readonly lastUpdatedDate = 'June 12, 03:36';

    readonly topVariations: TopVariation[] = [
        { nameKey: 'market_prices_page.products.mango', categoryKey: 'market_prices_page.categories.fruits', price: '530 XAF', change: '+18.4%' },
        { nameKey: 'market_prices_page.products.pepper_hot', categoryKey: 'market_prices_page.categories.vegetables', price: '2 063 XAF', change: '+9.4%' },
        { nameKey: 'market_prices_page.products.coffee_arabica', categoryKey: 'market_prices_page.categories.cash_crops', price: '4 238 XAF', change: '+8.9%' },
        { nameKey: 'market_prices_page.products.cayenne_pepper', categoryKey: 'market_prices_page.categories.vegetables', price: '2 750 XAF', change: '+8.1%' },
        { nameKey: 'market_prices_page.products.avocado', categoryKey: 'market_prices_page.categories.fruits', price: '1 500 XAF', change: '+7.8%' },
        { nameKey: 'market_prices_page.products.organic_bell_pepper', categoryKey: 'market_prices_page.categories.vegetables', price: '4 667 XAF', change: '+7.1%' },
        { nameKey: 'market_prices_page.products.ginger', categoryKey: 'market_prices_page.categories.vegetables', price: '2 250 XAF', change: '+6.2%' },
        { nameKey: 'market_prices_page.products.penja_white_pepper', categoryKey: 'market_prices_page.categories.cash_crops', price: '9 350 XAF', change: '+6.1%' },
    ];

    // key = stable value matched against row.category, labelKey = i18n key for the translated label
    readonly categories: CategoryOption[] = [
        { key: 'All', labelKey: 'market_prices_page.categories.all' },
        { key: 'Grains', labelKey: 'market_prices_page.categories.grains' },
        { key: 'Roots & Tubers', labelKey: 'market_prices_page.categories.roots_tubers' },
        { key: 'Fruits', labelKey: 'market_prices_page.categories.fruits' },
        { key: 'Vegetables', labelKey: 'market_prices_page.categories.vegetables' },
        { key: 'Cash Crops', labelKey: 'market_prices_page.categories.cash_crops' },
        { key: 'Legumes', labelKey: 'market_prices_page.categories.legumes' },
        { key: 'Oils', labelKey: 'market_prices_page.categories.oils' },
        { key: 'Livestock', labelKey: 'market_prices_page.categories.livestock' },
    ];

    readonly countries: CountryOption[] = [
        { key: 'All', flag: '🌍', labelKey: 'market_prices_page.countries.all' },
        { key: 'Cameroon', flag: '🇨🇲', labelKey: 'market_prices_page.countries.cameroon' },
        { key: 'Chad', flag: '🇹🇩', labelKey: 'market_prices_page.countries.chad' },
        { key: 'C.A.R', flag: '🇨🇫', labelKey: 'market_prices_page.countries.car' },
        { key: 'Congo', flag: '🇨🇬', labelKey: 'market_prices_page.countries.congo' },
        { key: 'Gabon', flag: '🇬🇦', labelKey: 'market_prices_page.countries.gabon' },
        { key: 'Eq. Guinea', flag: '🇬🇶', labelKey: 'market_prices_page.countries.eq_guinea' },
    ];

    readonly buyingTips: BuyingTip[] = [
        { marketKey: 'market_prices_page.buying_tips.garoua_market', cityKey: 'market_prices_page.buying_tips.city_garoua', savingsPercent: 15 },
        { marketKey: 'market_prices_page.buying_tips.mokolo_market', cityKey: 'market_prices_page.buying_tips.city_yaounde', savingsPercent: 6 },
        { marketKey: 'market_prices_page.buying_tips.bangui_market', cityKey: 'market_prices_page.buying_tips.city_bangui', savingsPercent: 4 },
        { marketKey: 'market_prices_page.buying_tips.new_bell_market', cityKey: 'market_prices_page.buying_tips.city_douala', savingsPercent: 4 },
        { marketKey: 'market_prices_page.buying_tips.sandaga_market', cityKey: 'market_prices_page.buying_tips.city_douala', savingsPercent: 2 },
    ];

    constructor(private translate: TranslateService) {}

    private parseChange(c: string): number {
        return parseFloat(c.replace(/[^0-9.\-]/g, '')) || 0;
    }

    private matchesCountry(row: MarketPrice): boolean {
        return this.country === 'All' || row.country.includes(this.country);
    }

    private matchesSearch(row: MarketPrice): boolean {
        return (
            this.search === '' ||
            row.product.toLowerCase().includes(this.search.toLowerCase()) ||
            row.market.toLowerCase().includes(this.search.toLowerCase())
        );
    }

    get filtered(): MarketPrice[] {
        return this.allPrices.filter((row) => {
            const matchCat = this.category === 'All' || row.category === this.category;
            return matchCat && this.matchesCountry(row) && this.matchesSearch(row);
        });
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
    }

    get currentPage(): number {
        return Math.min(this.page, this.totalPages);
    }

    get pageStart(): number {
        return (this.currentPage - 1) * this.pageSize;
    }

    get pageRows(): MarketPrice[] {
        return this.filtered.slice(this.pageStart, this.pageStart + this.pageSize);
    }

    get showingEnd(): number {
        return Math.min(this.pageStart + this.pageSize, this.filtered.length);
    }

    get pageNumbers(): number[] {
        return Array.from({ length: this.totalPages }, (_, idx) => idx + 1);
    }

    get snapshotGroups(): SnapshotGroup[] {
        return this.categories
            .filter((c) => c.key !== 'All')
            .map((c) => ({
                catKey: c.labelKey,
                items: this.allPrices
                    .filter((r) => r.category === c.key && this.matchesCountry(r))
                    .sort((a, b) => this.parseChange(b.change) - this.parseChange(a.change))
                    .slice(0, 4),
            }))
            .filter((g) => g.items.length > 0);
    }

    get activeCountry(): CountryOption {
        return this.countries.find((c) => c.key === this.country) ?? this.countries[0];
    }

    badgeColor(trend: string): string {
        if (trend === '↗') return 'trend-up';
        if (trend === '↘') return 'trend-down';
        return 'trend-neutral';
    }

    setCountry(key: string): void {
        this.country = key;
        this.page = 1;
    }

    onSearch(event: Event): void {
        this.search = (event.target as HTMLInputElement).value;
        this.page = 1;
    }

    onCategory(event: Event): void {
        this.category = (event.target as HTMLSelectElement).value;
        this.page = 1;
    }

    setPage(p: number): void {
        this.page = p;
    }

    prevPage(): void {
        this.page = Math.max(1, this.currentPage - 1);
    }

    nextPage(): void {
        this.page = Math.min(this.totalPages, this.currentPage + 1);
    }
}